import http from 'axios'
import { initializeApp } from 'firebase/app'
import { getAnalytics } from 'firebase/analytics'
import { getAuth, signInAnonymously } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import {
  getStorage,
  ref,
  getDownloadURL,
  uploadBytesResumable
} from 'firebase/storage'
import firebaseConfig from '@/config/firebase'
import i18n from '@/plugins/i18n'

const callBackend = async (endpoint, data) => {
  
  const backendUrl = import.meta.env.VITE_BACKEND_URL
  // Payments router lives under /api/v2/payments; other modules mount at root.
  const isPaymentsRoute = endpoint.startsWith('payments/')
  const path = isPaymentsRoute ? `api/v2/${endpoint}` : endpoint

  // El backend exige un ID token de Firebase en todas las rutas no públicas.
  // ensureAuth() recupera la sesión si el navegador la perdió (#48), para que
  // la petición no salga nunca sin Authorization.
  const user = await ensureAuth()
  const idToken = await user.getIdToken()
  const headers = { Authorization: `Bearer ${idToken}` }

  const res = await http.post(`${backendUrl}/${path}`, data, { headers })
  return res.data
}

const firebaseApp = initializeApp(firebaseConfig)

// La inscripción es anónima: la sesión vive en el almacenamiento del navegador
// (IndexedDB → localStorage → …), que Safari/ITP puede borrar entre visitas y
// que la navegación privada descarta al cerrar la pestaña. Sin sesión, cualquier
// escritura en Firestore falla con `permission-denied`. ensureAuth() garantiza
// que hay usuario antes de escribir.
let _authPromise = null

const ensureAuth = async () => {
  const auth = getAuth(firebaseApp)
  // currentUser es null mientras el SDK restaura la sesión desde IndexedDB.
  await auth.authStateReady()
  if (auth.currentUser) return auth.currentUser
  // El memo evita que dos escrituras concurrentes creen dos cuentas anónimas.
  if (!_authPromise) {
    _authPromise = signInAnonymously(auth)
      .then(({ user }) => user)
      .finally(() => { _authPromise = null })
  }
  return _authPromise
}

const waiverStorage = getStorage(firebaseApp, 'gs://owqlo-waivers-production-id')
const getWaiverUrl = (storagePath) => getDownloadURL(ref(waiverStorage, storagePath))

const formatErrorMessageCallBackend = error => {
  const errorMessage = error.response?.data?.message || i18n.global.t('common.unexpectedError')
  return { error: errorMessage }
}

const uploadFileAndGetUrl = async (storagePath, file) => {
  // For images
  if (!(file instanceof File)) return file // this happens when the file has been readed from the database, so it is just an URL, or when the file has been deleted an is null
  const storage = getStorage()
  const storageRef = ref(storage, `${storagePath}/${file.name}`)

  const uploadTask = uploadBytesResumable(storageRef, file)

  await uploadTask

  // Get the download URL
  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
  return downloadURL
}

const uploadFileAndGetObj = async (storagePath, file) => {
  // For non-images
  if (!(file instanceof File)) return file // this happens when the file has been readed from the database, so it is a simple object
  const storage = getStorage()
  const storageRef = ref(storage, `${storagePath}/${file.name}`)

  const uploadTask = uploadBytesResumable(storageRef, file)

  await uploadTask

  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)

  return {
    publicUrl: downloadURL,
    name: file.name,
    type: file.type,
    sizeInBytes: file.size,
    storagePath: `${storagePath}/`
  }
}

const db = getFirestore(firebaseApp)
const analytics = getAnalytics(firebaseApp)

export {
  firebaseApp,
  db,
  analytics,
  callBackend,
  ensureAuth,
  formatErrorMessageCallBackend,
  uploadFileAndGetUrl,
  uploadFileAndGetObj,
  waiverStorage,
  getWaiverUrl,
}
